import { Variable } from '~/packages/variable/variables.typing'
import { dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'
import productMetricIdsStatsAction from './productMetricIdsStats.action.json' with { type: 'json' }
import z from 'zod'

const combinedShape = object({
  id: string().label('ID', 'Identificador', 'Código'),
  productCode: string().isOptional().label('Código do produto'),
  productName: string().isOptional().label('Nome do produto'),
  cluster: string().isOptional().label('Cluster'),
  portfolio: string().isOptional().label('Portfólio'),
  sebraeResponsavel: string().isOptional().label('SEBRAE Responsável'),
  productActiveInactive: string().isOptional().label('Produto ativo/inativo'),
  instantFixedPortfolio: string().isOptional().label('Portfólio instantâneo/fixo'),
  instrument: string().isOptional().label('Instrumento'),
  modality: string().isOptional().label('Modalidade'),
  activationDate: dateTime().isOptional().label('Data de ativação'),
  productAgeInMonths: number().isOptional().label('Idade do produto em meses'),
  attendanceLast3Months: number().isOptional().label('Atendimentos nos últimos 3 meses'),
  attendanceLast12Months: number().isOptional().label('Atendimentos nos últimos 12 meses'),
  attendanceLast36Months: number().isOptional().label('Atendimentos nos últimos 36 meses'),
  classesLast3Months: number().isOptional().label('Aulas nos últimos 3 meses'),
  classesLast12Months: number().isOptional().label('Aulas nos últimos 12 meses'),
  classesLast36Months: number().isOptional().label('Aulas nos últimos 36 meses'),
  dateOfLastService: dateTime().isOptional().label('Data do último atendimento'),
  daysSinceLastAppointment: number().isOptional().label('Dias desde o último atendimento'),
  monthsWithServiceLast12: number()
    .isOptional()
    .label('Meses com atendimento nos últimos 12 meses'),
  scopeLast12Months: number().isOptional().label('Escopo nos últimos 12 meses'),
  courseLifeCycleScore: number().isOptional().label('Pontuação do ciclo de vida do curso'),
  completionRate: number().isOptional().label('Taxa de conclusão'),
  indexNps: number().isOptional().label('Índice NPS'),
  indexIds: number().label('Índice IDS'),
  classificationBcg: string().isOptional().label('Classificação BCG'),
  uf: enumerated({
    BR: 'Brasil (nacional)',
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins',
  }).label('Unidade Federativa'),
  createdAt: dateTime().isOptional().label('Data de criação'),
})
export const productMetricIdsExpression = expression(combinedShape.shape)

const combinedShapeZod = z.object({
  id: z.string(),
  productCode: z.string(),
  productName: z.string(),
  cluster: z.string().nullable(),
  portfolio: z.string().nullable(),
  sebraeResponsavel: z.string().nullable(),
  productActiveInactive: z.string().nullable(),
  instantFixedPortfolio: z.string().nullable(),
  instrument: z.string().nullable(),
  modality: z.string().nullable(),
  activationDate: z.date().nullable(),
  productAgeInMonths: z.number().nullable(),
  attendanceLast3Months: z.number().nullable(),
  attendanceLast12Months: z.number().nullable(),
  attendanceLast36Months: z.number().nullable(),
  classesLast3Months: z.number().nullable(),
  classesLast12Months: z.number().nullable(),
  classesLast36Months: z.number().nullable(),
  dateOfLastService: z.date().nullable(),
  daysSinceLastAppointment: z.number().nullable(),
  monthsWithServiceLast12: z.number().nullable(),
  scopeLast12Months: z.number().nullable(),
  courseLifeCycleScore: z.number(),
  completionRate: z.number().nullable(),
  indexNps: z.number(),
  indexIds: z.number(),
  classificationBcg: z.string().nullable(),
  uf: z
    .enum([
      'BR',
      'AC',
      'AL',
      'AP',
      'AM',
      'BA',
      'CE',
      'DF',
      'ES',
      'GO',
      'MA',
      'MT',
      'MS',
      'MG',
      'PA',
      'PB',
      'PR',
      'PE',
      'PI',
      'RJ',
      'RN',
      'RS',
      'RO',
      'RR',
      'SC',
      'SP',
      'SE',
      'TO',
    ])
    .describe('Unidade Federativa'),
  createdAt: z.date(),
})
export type ProductMetricIds = z.infer<typeof combinedShapeZod>

export const productMetricIdsVariable: Variable = {
  key: 'productMetricIds',
  categoryKey: 'generalPortfolio',
  categoryKeys: ['generalPortfolio'],
  actionDefinitionKey: productMetricIdsStatsAction.key,
  aggregationMode: 'mean',
  customValue: {
    type: 'expression',
    kind: 'function',
    name: 'avg',
    arguments: [
      {
        type: 'expression',
        kind: 'property',
        property: 'indexIds',
        literalType: 'number',
      },
    ],
  },
  title: 'Índice de Desempenho de Solução (IDS)',
  unitSingular: 'solução',
  unitPlural: 'soluções',
  unitFormatSuffix: {
    percentage: '',
  },
  unitFormat: 'percentage',
  colorMap: ['#e74c3c', '#ffd400', '#26d07c'],
  mapValueDomain: {
    min: 0,
    max: {
      source: 'seriesMax',
      whenLessThanOrEqual: 30,
      fallback: 100,
    },
  },
  mapColorDomain: [0, 30, 'max'],
  grammaticalGender: 'female',
  timeType: 'currentOnly',
  shape: combinedShape,
  betterDensity: 'more',
  relevance: 0.5,
  basicSelect: [],
  detailedSelect: [],
  filterableBy: undefined,
  compatibleChildrenTypes: ['country', 'state'],
}

const productMetricIdsStatsDefinition = defineAction({
  ...productMetricIdsStatsAction,
  input: object({
    variable,
    highlight: productMetricIdsExpression.isOptional(),
    filter: productMetricIdsExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForCurrentOnly().isOptional(),
    select: select(combinedShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default productMetricIdsStatsDefinition
